import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import Tabulation from 'components/Tabulation';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { actions as notificationsActions } from 'redux/notifications';
import { actions as modalActions } from 'redux/modal';
import { transformTableData, getConfirmationModalConfig } from 'utils';
import { useTranslation } from 'react-i18next';
import {
  getNotificationsList,
  getUnreadNotificationsCount,
  markNotificationAsRead,
  markNotificationAsUnread,
  deleteNotification,
  bulkMarkNotificationsAsRead,
  bulkDeleteNotifications,
} from 'http/notifications';
import { useDispatch } from 'react-redux';
import { useAsyncFn } from 'react-use';
import getTableConfig from './helpers/getTableConfig';

const NOTIFICATION_TYPE_OPTIONS = [
  'Reminder',
  'Alert',
  'Action Required',
  'Information',
  'Regulatory Update',
  'System Notice',
];

const UNREAD_TAB = 'unread';
const ALL_TAB = 'all';

const Notifications = ({ tableData }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const tableConfig = useMemo(() => getTableConfig(), [t]);
  const [columns, notifications] = transformTableData(tableConfig, tableData.data);
  const [activeTab, setActiveTab] = useState({ tabIndex: 0, alias: UNREAD_TAB });

  const tabsData = useMemo(
    () => [
      { label: t('notifications.unread'), alias: UNREAD_TAB },
      { label: t('notifications.all'), alias: ALL_TAB },
    ],
    [t],
  );

  const refreshNotificationsCount = useCallback(async () => {
    const result = await getUnreadNotificationsCount();
    if (result && !result.error && result.count !== undefined) {
      dispatch(notificationsActions.setNewNotifications(result.count));
    }
  }, [dispatch]);

  const handleCloseModal = () => dispatch(modalActions.closeModal());

  const [, runRowAction] = useAsyncFn(async (action, id) => {
    const result = await action(id);
    if (!result?.error) {
      tableData.handleForceUpdate();
      await refreshNotificationsCount();
    }
  }, []);

  const handleToggleReadStatus = (id, readAt) =>
    runRowAction(readAt ? markNotificationAsUnread : markNotificationAsRead, id);

  const handleDeleteNotification = id => {
    const [config, element] = getConfirmationModalConfig({
      title: t('notifications.deleteModalTitle'),
      confirmAction: async () => {
        const result = await deleteNotification(id);
        if (!result?.error) {
          tableData.handleForceUpdate();
          await refreshNotificationsCount();
          handleCloseModal();
        }
      },
      rejectAction: handleCloseModal,
      message: t('notifications.deleteConfirmationMessage'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const currentPageIds = useMemo(
    () => (Array.isArray(tableData.data) ? tableData.data.map(row => row.id) : []),
    [tableData.data],
  );

  const handleBulkRead = async () => {
    if (!currentPageIds.length) {
      return;
    }
    const result = await bulkMarkNotificationsAsRead(currentPageIds);
    if (!result?.error) {
      tableData.handleForceUpdate();
      await refreshNotificationsCount();
    }
  };

  const handleBulkDelete = () => {
    if (!currentPageIds.length) {
      return;
    }
    const [config, element] = getConfirmationModalConfig({
      title: t('notifications.deleteAllModalTitle'),
      confirmAction: async () => {
        const result = await bulkDeleteNotifications(currentPageIds);
        if (!result?.error) {
          tableData.handleForceUpdate();
          await refreshNotificationsCount();
          handleCloseModal();
        }
      },
      rejectAction: handleCloseModal,
      message: t('notifications.deleteAllConfirmationMessage'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const handleTabChange = (event, data) => {
    setActiveTab({ tabIndex: data.tabIndex, alias: data.alias });
    if (data.alias === UNREAD_TAB) {
      tableData.handleFiltering('isNew', 'true');
    } else {
      tableData.handleRemoveFilter('isNew');
    }
  };

  useEffect(() => {
    refreshNotificationsCount();
  }, []);

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        getIcon: rowData => (rowData.readAt ? 'markunread' : 'drafts'),
        getTitle: rowData => (rowData.readAt ? t('notifications.markAsUnread') : t('notifications.markAsRead')),
        getOnClick: rowData => () => handleToggleReadStatus(rowData.id, rowData.readAt),
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        title: t('buttons.delete'),
        onClick: event => handleDeleteNotification(event.currentTarget.dataset.key),
      },
    ],
    [t, handleToggleReadStatus, handleDeleteNotification],
  );

  const isLoading = tableData.isFetchingTableData;

  const tableComponent = (
    <ModalBusyContainer busy={isLoading}>
      <AppTable
        columns={columns}
        data={notifications}
        totalCount={tableData.totalCount}
        rowsPerPage={tableData.pageSize}
        onChangePage={tableData.handleChangePage}
        onColumnSorting={tableData.handleSorting}
        currentPage={tableData.page}
        getRowStyle={row => (!row.readAt ? { backgroundColor: '#f5f5f5' } : {})}
        isLazyLoading
        actions={tableActions}
      />
    </ModalBusyContainer>
  );

  return (
    <PageLayout
      pageTitle={t('notifications.pageTitle')}
      actionSectionRight={[
        {
          type: 'button',
          icon: 'drafts',
          title: t('notifications.readAll'),
          onClick: handleBulkRead,
          disabled: !currentPageIds.length,
        },
        {
          type: 'button',
          icon: 'delete',
          title: t('notifications.deleteAll'),
          onClick: handleBulkDelete,
          disabled: !currentPageIds.length,
        },
      ]}
      pageContentActions={[
        {
          type: 'component',
          Component: (
            <TableFilterToolbar
              filters={[
                {
                  type: 'select',
                  name: 'notificationType',
                  label: t('tablesLabels.notificationType'),
                  options: NOTIFICATION_TYPE_OPTIONS.map(value => ({
                    value,
                    label: value,
                  })),
                },
              ]}
              filtering={tableData.filtering}
              onFilterChange={tableData.handleFiltering}
              onRemoveFilter={tableData.handleRemoveFilter}
              onClearFilters={tableData.handleClearFilters}
            />
          ),
        },
      ]}
    >
      <Tabulation
        tabsData={tabsData}
        activeTab={activeTab.tabIndex}
        onTabChange={handleTabChange}
        commonComponent={tableComponent}
      />
    </PageLayout>
  );
};

Notifications.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable(
  {
    defaultSorting: {
      field: 'createdAt',
      dir: 'desc',
    },
    defaultFiltering: { isNew: 'true' },
    fetchingData: 'items',
  },
  getNotificationsList,
)(Notifications);
